/** @format */

import React from "react";
import { Box, Skeleton, Tooltip } from "@mui/material";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";

// Friendly labels for the raw intent keys stored in the audit log. Anything
// not listed falls back to a Title Case of the key.
const INTENT_LABELS = {
  ndr_analysis: "NDR Analysis",
  ndr: "NDR Analysis",
  rto_analysis: "RTO Performance",
  rto: "RTO Performance",
  sla_analysis: "SLA Adherence",
  sla: "SLA Adherence",
  tat_analysis: "TAT Adherence",
  forward_shipments: "Forward Shipments",
  pickup_performance: "Pickup Performance",
  carrier_performance: "Carrier Performance",
  delivery_performance: "Delivery Performance",
  zone_analysis: "Zone Analysis",
  general_analytics: "General Analytics",
};

export const humanizeIntent = (key) => {
  if (!key) return "—";
  if (INTENT_LABELS[key]) return INTENT_LABELS[key];
  return key
    .replace(/[._]/g, " ")
    .split(" ")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
};

const StatCard = ({ icon, value, label, sublabel, gradient, iconColor, tooltip }) => {
  const card = (
    <Box
      sx={{
        position: "relative",
        flex: "1 1 0",
        minWidth: 132,
        p: 1.25,
        borderRadius: "14px",
        background: gradient,
        border: "1px solid rgba(255,255,255,0.6)",
        boxShadow: "0 4px 14px -6px rgba(15,23,42,0.18)",
        overflow: "hidden",
        transition: "transform 160ms ease, box-shadow 160ms ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 10px 24px -8px rgba(15,23,42,0.28)",
        },
      }}
    >
      {/* decorative glow */}
      <Box
        sx={{
          position: "absolute",
          top: -24,
          right: -24,
          width: 72,
          height: 72,
          borderRadius: "50%",
          backgroundColor: "rgba(255,255,255,0.35)",
          filter: "blur(2px)",
        }}
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          position: "relative",
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "9px",
            backgroundColor: "rgba(255,255,255,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            boxShadow: "0 2px 6px -2px rgba(15,23,42,0.2)",
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: 16, color: iconColor } })}
        </Box>
        <Box
          sx={{
            fontSize: 22,
            fontWeight: 800,
            lineHeight: 1.05,
            color: "#0f172a",
            letterSpacing: "-0.02em",
            wordBreak: "break-word",
          }}
        >
          {value}
        </Box>
      </Box>
      <Box
        sx={{
          position: "relative",
          mt: 0.6,
          fontSize: 10.5,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          color: "#475569",
        }}
      >
        {label}
      </Box>
      {sublabel && (
        <Box
          sx={{
            position: "relative",
            mt: 0.1,
            fontSize: 10.5,
            fontWeight: 500,
            color: "#64748b",
          }}
        >
          {sublabel}
        </Box>
      )}
    </Box>
  );
  return tooltip ? (
    <Tooltip title={tooltip} arrow placement="top">
      {card}
    </Tooltip>
  ) : (
    card
  );
};

const CardsRow = ({ children }) => (
  <Box
    sx={{
      display: "flex",
      flexWrap: "wrap",
      gap: 1.25,
      width: "100%",
      maxWidth: 860,
    }}
  >
    {children}
  </Box>
);

const UserAnalyticsCards = ({ stats, loading }) => {
  if (loading) {
    return (
      <CardsRow>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton
            key={i}
            variant="rounded"
            sx={{
              flex: "1 1 0",
              minWidth: 132,
              height: 84,
              borderRadius: "14px",
            }}
          />
        ))}
      </CardsRow>
    );
  }

  if (!stats) return null;

  const topTopic =
    stats.top_topics && stats.top_topics.length
      ? stats.top_topics[0]
      : null;

  return (
    <CardsRow>
      <StatCard
        icon={<QueryStatsRoundedIcon />}
        value={stats.total_questions ?? 0}
        label="Questions asked"
        sublabel={
          stats.questions_today
            ? `${stats.questions_today} today`
            : "all time"
        }
        gradient="linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)"
        iconColor="#6366f1"
        tooltip="Total ProPilot questions you've asked"
      />
      <StatCard
        icon={<CalendarMonthRoundedIcon />}
        value={stats.questions_this_week ?? 0}
        label="This week"
        sublabel={
          stats.questions_this_month
            ? `${stats.questions_this_month} this month`
            : null
        }
        gradient="linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)"
        iconColor="#059669"
        tooltip="Questions asked since Monday"
      />
      <StatCard
        icon={<FavoriteRoundedIcon />}
        value={stats.helpful_count ?? 0}
        label="Helpful answers"
        sublabel="you marked correct"
        gradient="linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)"
        iconColor="#ea580c"
        tooltip="Answers you rated 👍"
      />
      <StatCard
        icon={<EmojiEventsRoundedIcon />}
        value={topTopic ? humanizeIntent(topTopic.intent) : "—"}
        label="Top topic"
        sublabel={
          topTopic ? `asked ${topTopic.count}×` : "ask your first question"
        }
        gradient="linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)"
        iconColor="#9333ea"
        tooltip="The topic you explore most"
      />
    </CardsRow>
  );
};

export default UserAnalyticsCards;
